import React, { FC, ReactNode, useEffect, useMemo, useState } from 'react'
import styled from '~/app/components/styled'
import BottomSheetWithItemText from '~/app/components/common/BottomSheet/BottomSheetWithItemText'
import useModalHash from '~/app/components/common/useModalHash'
import moment from 'moment'
import { Typography } from '@yanolja-rnd/ya-com-core'

/**
 * CategoryItem component
 * author: soojin.oh
 */

// type definitions
type Props = {
  items: { id: string; title: string }[]
  selectedDate: DURATION
  setSelectedDate: (duration: DURATION) => void
  id: string
  currentDate: string
  rightAppended?: ReactNode
}

export type DURATION = [moment.Moment | null, moment.Moment | null]

// styled components
const Container = styled.div`
  border-radius: 0.4rem;
  border: solid 0.1rem ${({ theme }) => theme.color.gray400};
  padding: 1.1rem 0.8rem;
  margin-right: 0.8rem;
  cursor: pointer;
  height: 3.6rem;
  display: inline-flex;
  justify-content: space-between;
  align-items: center;
  white-space: nowrap;
  flex: 1;

  ${Typography('body1').regular}
  :first-of-type {
    margin-left: 2rem;
  }
  :last-of-type {
    margin-right: 2rem;
  }

  @media (hover: hover) and (pointer: fine) {
    :hover {
      background: ${({ theme }) => theme.color.gray100};
    }
  }

  :active {
    background: ${({ theme }) => theme.color.gray200};
  }
`

// CategoryItem component
const DatePeriod: FC<Props> = function ({
  items,
  selectedDate,
  setSelectedDate,
  id,
  currentDate,
  rightAppended,
}) {
  const [isOpened, setOpened] = useModalHash({ id })
  const [selectedId, setSelectedId] = useState('')
  const period = useMemo(() => {
    const item = items.filter(item => {
      const [from, to] = selectedDate
      return item.id === `${to?.diff(from, 'days') ?? items[0].id}`
    })?.[0]
    return {
      title: item?.title,
      selectedPeriod: item?.id,
    }
  }, [selectedDate])

  /**
   * Error: Loading initial props cancelled
   * -> 기간 변경시 오류 발생
   *
   * BottomSheet 가 닫히면서 hash 변경
   * -> 바로 setSelectedDate 를 실행하게 되면 router 가 두번 이동하게 되는데 이때 에러가 발생함.
   *
   * 순서를 보장해주기 위해 hash 를 변경하고 useEffect 를 통해 selectedDate 를 변경해줌.
   */
  useEffect(() => {
    if (!isOpened && selectedId) {
      setSelectedDate([moment(currentDate).subtract(selectedId, 'days'), moment(currentDate)])
    }
  }, [isOpened, selectedId])

  return (
    <>
      <Container onClick={() => setOpened(true)}>
        {period.title}
        {rightAppended}
      </Container>
      <BottomSheetWithItemText
        title={'기간 선택'}
        isOpened={isOpened}
        onRequestClose={() => setOpened(false)}
        items={items}
        selectedId={period.selectedPeriod}
        onItemSelected={id => {
          setOpened(false)
          setSelectedId(id)
        }}
        onBackgroundClick={() => setOpened(false)}
      />
    </>
  )
}

export default DatePeriod
